import { Command } from 'cmdk';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { Button } from './button';
import { Dialog } from './dialog';
import { Field, Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './menu';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  emptyLabel = 'Nothing found.',
  onCreate,
  onAddNew,
  createLabel = 'Create',
  disabled,
  loading,
  className,
  size = 'md',
  invalid,
  align = 'start'
}: {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  /**
   * When provided, the list ends with a "<createLabel>" item that opens a
   * small name dialog, and offers "<createLabel> <typed text>" while searching.
   */
  onCreate?: (name: string) => void | Promise<void>;
  /**
   * Like `onCreate`, but the caller owns the form: "Add new" (and "Create
   * <typed text>") call this with the typed text instead of opening the
   * built-in name dialog. For records that need more than a name, like a
   * billing party with emails, or a template with an amount.
   */
  onAddNew?: (prefill: string) => void;
  createLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  invalid?: boolean;
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const formId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Inside a modal dialog the popover must be modal too. The dialog's scroll
  // lock swallows wheel events everywhere outside its own subtree, and the
  // list is portaled to <body>, so without this a long list (templates in the
  // credit/fee dialog) could not be scrolled.
  const [inDialog, setInDialog] = useState(false);

  const offersCreate = Boolean(onCreate || onAddNew);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  const trimmed = search.trim();
  const canCreate =
    offersCreate &&
    trimmed.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === trimmed.toLowerCase());

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || !onCreate) return;
    setSaving(true);
    try {
      await onCreate(name);
      setAdding(false);
      setNewName('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Popover
        open={open}
        modal={inDialog}
        onOpenChange={(v) => {
          if (v) setInDialog(Boolean(triggerRef.current?.closest('[role="dialog"]')));
          setOpen(v);
          if (!v) setSearch('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-xl border bg-[var(--surface)] px-3 text-left text-sm transition-colors',
              size === 'sm' ? 'h-8' : 'h-9',
              invalid ? 'border-red-500' : 'border-[var(--line-strong)] hover:border-[var(--text-subtle)]',
              'focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/25',
              'disabled:cursor-not-allowed disabled:opacity-50',
              className
            )}
          >
            {loading && !selected ? (
              <span className="skeleton h-4 w-24" />
            ) : (
              <span
                className={cn('truncate', !selected && 'text-[var(--text-subtle)]')}
                title={selected?.label}
              >
                {selected?.label ?? placeholder}
              </span>
            )}
            <ChevronDown className="size-4 shrink-0 text-[var(--text-subtle)]" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align={align}
          collisionPadding={8}
          // At least as wide as the trigger, grows to fit the longest option, and
          // never past the viewport (Radix measures the room it has for us).
          className={cn(
            'w-max min-w-[var(--radix-popover-trigger-width)] p-0',
            'max-w-[min(32rem,var(--radix-popover-content-available-width,32rem))]'
          )}
        >
          <Command
            loop
            // Items are keyed by id and searched by label (passed as keywords).
            // Keep "Create …" pinned regardless of the fuzzy score.
            filter={(_itemValue, query, keywords) => {
              if (keywords?.includes('__create__')) return 1;
              const q = query.toLowerCase();
              return keywords?.some((k) => k.toLowerCase().includes(q)) ? 1 : 0;
            }}
          >
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search…"
              className="w-full border-b border-[var(--line)] bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-[var(--text-subtle)]"
            />
            <Command.List className="scroll-thin max-h-60 overflow-y-auto p-1">
              {!canCreate && !(offersCreate && !trimmed) ? (
                <Command.Empty className="px-3 py-6 text-center text-sm text-muted">
                  {emptyLabel}
                </Command.Empty>
              ) : null}

              {options.map((option) => (
                <Command.Item
                  key={option.value}
                  // cmdk tracks the highlighted item by `value`, so it must be
                  // unique: keyed by label, two options with the same name (two
                  // templates, two clients) highlighted together.
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                    setSearch('');
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm data-[selected=true]:bg-[var(--surface-hover)]"
                >
                  <Check
                    className={cn(
                      'size-4 shrink-0',
                      option.value === value ? 'opacity-100 text-[var(--accent)]' : 'opacity-0'
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.hint ? (
                    <span className="shrink-0 text-sm text-subtle">{option.hint}</span>
                  ) : null}
                </Command.Item>
              ))}

              {canCreate ? (
                <Command.Item
                  keywords={['__create__']}
                  value={`__create__${trimmed}`}
                  onSelect={async () => {
                    setOpen(false);
                    setSearch('');
                    if (onAddNew) onAddNew(trimmed);
                    else await onCreate?.(trimmed);
                  }}
                  className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border-t border-[var(--line)] px-2.5 py-2 text-sm text-[var(--accent)] data-[selected=true]:bg-[var(--surface-hover)]"
                >
                  <Plus className="size-4 shrink-0" />
                  <span className="truncate">
                    {createLabel} “{trimmed}”
                  </span>
                </Command.Item>
              ) : null}

              {offersCreate && !trimmed ? (
                <Command.Item
                  keywords={['__create__']}
                  value="__add_new__"
                  onSelect={() => {
                    setOpen(false);
                    if (onAddNew) {
                      onAddNew('');
                      return;
                    }
                    setNewName('');
                    setAdding(true);
                    // The closing popover hands focus back to its trigger after
                    // the dialog opens; take it back for the name field.
                    setTimeout(() => document.getElementById(`${formId}-name`)?.focus(), 50);
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-[var(--accent)] data-[selected=true]:bg-[var(--surface-hover)]',
                    options.length > 0 && 'mt-1 border-t border-[var(--line)]'
                  )}
                >
                  <Plus className="size-4 shrink-0" />
                  <span className="truncate">{createLabel}</span>
                </Command.Item>
              ) : null}
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>

      {onCreate && !onAddNew ? (
        <Dialog
          open={adding}
          onOpenChange={(v) => !saving && setAdding(v)}
          title={createLabel}
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setAdding(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                form={formId}
                loading={saving}
                disabled={!newName.trim()}
              >
                Add
              </Button>
            </>
          }
        >
          <form
            id={formId}
            onSubmit={(e) => {
              e.preventDefault();
              void submitNew();
            }}
          >
            <Field label="Name" htmlFor={`${formId}-name`}>
              <Input
                id={`${formId}-name`}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </Field>
          </form>
        </Dialog>
      ) : null}
    </>
  );
}
